import toast from 'react-hot-toast';
import { Phone, MapPin, Star, ExternalLink, AlarmClock, Copy } from 'lucide-react';
import { Avatar } from '@/shared/components/ui/Avatar';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { cn } from '@/shared/utils/utils';
import { STATUSES, STATUS_LABELS } from '../constants';
import type { Lead } from '../types';

interface LeadsTableProps {
  leads: Lead[];
  isLoading: boolean;
  isAdmin: boolean;
  onRowClick: (id: string) => void;
  onStatusChange: (id: string, newStatus: string) => void;
  onLogCall: (id: string, name: string) => void;
}

/** Status → badge tone classes (design-system tokens only). */
const STATUS_TONE: Record<string, string> = {
  new: 'bg-surface-variant text-on-surface-variant',
  contacted: 'bg-info/10 text-info',
  interested: 'bg-warning/10 text-warning',
  converted: 'bg-success/10 text-success',
  rejected: 'bg-danger/10 text-danger',
};

export function LeadsTable({
  leads,
  isLoading,
  isAdmin,
  onRowClick,
  onStatusChange,
  onLogCall,
}: LeadsTableProps) {
  const copy = (text: string, label: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <EmptyState
        icon={<Star className="h-6 w-6" />}
        title="No leads found"
        description="Adjust your filters or run the lead generator to populate your pipeline."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1000px] border-collapse text-left text-data-md text-on-surface">
        <thead className="sticky top-0 z-10 border-b border-outline bg-surface-container-low">
          <tr className="text-label-caps uppercase tracking-wider text-on-surface-variant">
            <th className="px-4 py-3 font-semibold">Name</th>
            <th className="px-4 py-3 font-semibold">Rating</th>
            <th className="px-4 py-3 font-semibold">Phone</th>
            <th className="px-4 py-3 font-semibold">Emails</th>
            <th className="px-4 py-3 font-semibold">Niche</th>
            <th className="px-4 py-3 font-semibold">Location</th>
            {isAdmin && <th className="px-4 py-3 font-semibold">Owner</th>}
            <th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 font-semibold">Created</th>
            <th className="px-4 py-3 text-right font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr
              key={lead.id}
              className="group cursor-pointer border-b border-outline bg-surface transition-colors last:border-0 hover:bg-surface-variant"
              onClick={() => onRowClick(lead.id)}
            >
              {/* Name + website */}
              <td className="px-4 py-3">
                <div className="max-w-[220px] truncate font-medium text-on-surface" title={lead.name}>
                  {lead.name}
                </div>
                {lead.website && (
                  <a
                    href={lead.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="mt-0.5 flex items-center gap-0.5 text-body-sm text-primary hover:underline"
                  >
                    {lead.website.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </td>

              {/* Rating */}
              <td className="px-4 py-3">
                {parseFloat(lead.rating || '0') > 0 ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-surface-variant px-2 py-0.5 text-body-sm font-medium text-on-surface">
                    <Star className="h-3 w-3 fill-warning text-warning" /> {lead.rating}
                  </span>
                ) : (
                  <span className="text-on-surface-variant/40">—</span>
                )}
              </td>

              {/* Phone */}
              <td className="px-4 py-3 tabular-nums text-on-surface-variant">
                {lead.phone ? (
                  <a
                    href={`tel:${lead.phone}`}
                    onClick={(e) => e.stopPropagation()}
                    className="hover:text-primary"
                  >
                    {lead.phone}
                  </a>
                ) : (
                  '—'
                )}
              </td>

              {/* Emails */}
              <td className="px-4 py-3">
                {lead.emails && lead.emails.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {lead.emails.slice(0, 2).map((e) => (
                      <button
                        key={e}
                        onClick={(evt) => copy(e, 'Email', evt)}
                        title="Click to copy"
                        className="inline-flex max-w-[160px] items-center gap-1 truncate rounded-full border border-outline bg-surface-variant px-2 py-0.5 text-body-sm text-on-surface-variant hover:text-on-surface"
                      >
                        <span className="truncate">{e}</span>
                        <Copy className="h-3 w-3 shrink-0" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <span className="italic text-on-surface-variant/40">None</span>
                )}
              </td>

              {/* Niche */}
              <td className="px-4 py-3">
                {lead.niche ? (
                  <span className="rounded-full bg-primary-container px-2 py-0.5 text-body-sm font-medium text-on-primary-container">
                    {lead.niche}
                  </span>
                ) : (
                  '—'
                )}
              </td>

              {/* Location */}
              <td className="max-w-[150px] truncate px-4 py-3 text-on-surface-variant" title={lead.location}>
                {lead.location?.split('|')[0] || '—'}
              </td>

              {/* Owner */}
              {isAdmin && (
                <td className="px-4 py-3">
                  {lead.owner_name ? (
                    <span className="inline-flex items-center gap-2">
                      <Avatar name={lead.owner_name} size="sm" />
                      <span className="text-on-surface-variant">{lead.owner_name}</span>
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
              )}

              {/* Status (inline editable) */}
              <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                <select
                  value={lead.status}
                  onChange={(e) => onStatusChange(lead.id, e.target.value)}
                  aria-label={`Status for ${lead.name}`}
                  className={cn(
                    'cursor-pointer rounded-full border-0 px-2.5 py-1 text-label-caps font-semibold uppercase outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                    STATUS_TONE[lead.status] ?? STATUS_TONE.new,
                  )}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s} className="bg-surface normal-case text-on-surface">
                      {STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
                {lead.sub_status && (
                  <div className="mt-1 truncate pl-1 text-body-sm text-on-surface-variant">
                    {lead.sub_status.replace(/_/g, ' ')}
                  </div>
                )}
                {lead.follow_up_date && (
                  <div className="mt-0.5 flex items-center gap-1 pl-1 text-body-sm text-warning">
                    <AlarmClock className="h-3 w-3" />
                    {new Date(lead.follow_up_date).toLocaleDateString()}
                  </div>
                )}
              </td>

              {/* Created */}
              <td className="px-4 py-3 tabular-nums text-on-surface-variant">
                {new Date(lead.created_at).toLocaleDateString()}
              </td>

              {/* Actions */}
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onLogCall(lead.id, lead.name);
                    }}
                    className="rounded-md border border-outline p-1.5 text-on-surface-variant transition-colors hover:bg-success/10 hover:text-success"
                    title="Log a call"
                    aria-label={`Log a call for ${lead.name}`}
                  >
                    <Phone className="h-3.5 w-3.5" />
                  </button>
                  {lead.phone && (
                    <button
                      onClick={(e) => copy(lead.phone!, 'Phone', e)}
                      className="rounded-md border border-outline p-1.5 text-on-surface-variant transition-colors hover:bg-surface-variant hover:text-on-surface"
                      title="Copy phone"
                      aria-label={`Copy phone for ${lead.name}`}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {lead.maps_url && (
                    <a
                      href={lead.maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="rounded-md border border-outline p-1.5 text-on-surface-variant transition-colors hover:bg-surface-variant hover:text-on-surface"
                      title="Open on Google Maps"
                      aria-label={`Open ${lead.name} on Google Maps`}
                    >
                      <MapPin className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
